import { Component, inject, Input } from '@angular/core'
import HttpError, { FormErrors } from '@ui/HttpError'
import {
	FormValidationComponent,
	ValidationMessages,
} from '@ui/Components/Form/FormValidation.Component'
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { AccountService } from '@account/Services/AccountService'
import {
	InputValue,
	TextInputComponent,
} from '@ui/Components/Form/TextInput/TextInput.Component'
import { TwoFactorError } from '@account/Errors/TwoFactorError'
import { startAuthentication } from '@simplewebauthn/browser'
import { WebAuthnService } from '@account/Services/WebAuthnService'
import { NgStyle } from '@angular/common'
import { FlatButtonComponent } from '@ui/Components/Form/Button/FlatButton.Component'
import { FormErrorComponent } from '@ui/Components/Form/Error/FormError.Component'
import { RouterLink } from '@angular/router'

@Component({
	selector: 'login-form',
	templateUrl: './LoginForm.Component.html',
	styleUrls: ['../../Auth.css'],
	imports: [
		NgStyle,
		FormsModule,
		ReactiveFormsModule,
		TextInputComponent,
		FlatButtonComponent,
		FormErrorComponent,
		RouterLink,
	],
})
export class LoginFormComponent extends FormValidationComponent {
	@Input() public displayRegister: boolean

	@Input() private readonly loginSuccessful: () => void | Promise<void>

	@Input() private readonly showChallenge: () => void | Promise<void>

	public formErrors: FormErrors = {
		email: '',
		password: '',
	}

	public error: string | null = null

	protected validationMessages: ValidationMessages = {
		email: {
			required: 'Email is required',
		},
		password: {
			required: 'Password is required',
		},
	}

	private readonly accountService: AccountService

	private readonly webAuthnService: WebAuthnService

	constructor() {
		super()
		this.accountService = inject(AccountService)
		this.webAuthnService = inject(WebAuthnService)
	}

	public login = async (
		email: InputValue,
		password: InputValue,
	): Promise<void> => {
		this.validateForm()

		if (this.form.valid) {
			try {
				await this.accountService.login(email as string, password as string)

				this.loginSuccessful()
			} catch (error) {
				if (error instanceof TwoFactorError) {
					this.showChallenge()

					return
				}

				if (error instanceof HttpError) {
					if (error.getStatus() === 419) {
						window.location.reload()

						return
					}

					this.formErrors = error.handleFormErrors(this.formErrors, '')
					this.markInvalidFields()
				} else {
					throw error
				}
			}
		}
	}

	public startWebAuthn = async () => {
		try {
			const optionsJSON = await this.webAuthnService.getOptions()

			// Pass the options to the authenticator and wait for a response
			const assertionResponse = await startAuthentication({ optionsJSON })

			await this.webAuthnService.login(assertionResponse)

			this.loginSuccessful()
		} catch (error) {
			if (error instanceof HttpError) {
				this.error = error.message
			} else {
				this.error = 'An error occurred. Please try again.'
			}
		}
	}

	protected buildForm = (): void => {
		this.form = this.formBuilder.group({
			email: ['', [Validators.required]],
			password: ['', [Validators.required]],
		})
	}
}
